import { AangevraagdeVestiging, AangevraagdeVestigingOpeningstijd, Vestiging } from "@/entities/vestiging";
import { VerlengingStatus, VestigingStatus } from "@/entities/vestiging/constants";
import {
    Button,
    Checkbox,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Form,
    FormErrorMessage,
    FormFieldInputTime,
    Input,
    Label,
    Textarea,
    TextAreaFormField,
} from "@/shared/components";
import { timeRegex } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { VestigingCard, VestigingenUpdateHandler } from "../../shared";

export type BehandelAanvraagDialogType =
    | typeof VestigingStatus.BESTELD
    | "BesteldMetVerlenging"
    | "VerlengingAangevraagd"
    | typeof VestigingStatus.STOP_AANGEVRAAGD;

type BehandelAanvraagDialogFactoryProps = {
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    type: BehandelAanvraagDialogType;
    vestiging: Vestiging;
    onVestigingUpdate: VestigingenUpdateHandler;
};

export function BehandelAanvraagDialogFactory({
    open,
    onOpenChange,
    vestiging,
    type,
    onVestigingUpdate,
}: BehandelAanvraagDialogFactoryProps) {
    const [toelichting, setToelichting] = useState("");

    switch (type) {
        case "Besteld":
            return (
                <BehandelAanvraagDialog
                    open={open}
                    onOpenChange={onOpenChange}
                    title={`Start faciliteiten ${vestiging.beheerVestiging.vestigingNaam}`}
                    description="De regievoerder vraagt om te starten met faciliteiten bij deze vestiging."
                    vestiging={vestiging}
                    toelichting={toelichting}
                    setToelichting={setToelichting}
                    onVestigingUpdate={onVestigingUpdate}
                    type={type}
                />
            );
        case "BesteldMetVerlenging":
            return (
                <BehandelVerlengingDialog
                    open={open}
                    onOpenChange={onOpenChange}
                    title="Start faciliteiten en verlengen openingstijd"
                    description="De regievoerder vraagt om opening en verlenging openingstijd van deze vestiging. Neem contact op met de vestiging om te overleggen of dit mogelijk is. Spreek samen een nieuwe sluitingstijd af. Pas daarna de definitieve openingstijd aan."
                    vestiging={vestiging}
                    onVestigingUpdate={onVestigingUpdate}
                />
            );
        case "VerlengingAangevraagd":
            return (
                <BehandelVerlengingDialog
                    open={open}
                    onOpenChange={onOpenChange}
                    title="Verlenging openingstijd"
                    description="De regievoerder vraagt om een verlenging van de openingstijd voor deze vestiging. Neem contact op met de vestiging om te overleggen of dit mogelijk is. Spreek samen een nieuwe sluitingstijd af. Pas daarna de definitieve openingstijd aan."
                    vestiging={vestiging}
                    onVestigingUpdate={onVestigingUpdate}
                />
            );
        case "StopAangevraagd":
            return (
                <BehandelAanvraagDialog
                    open={open}
                    onOpenChange={onOpenChange}
                    title={`Stoppen faciliteiten ${Vestiging.getDisplayName(vestiging)} (regievoerder)`}
                    description="De regievoerder heeft gevraagd om de faciliteiten bij deze vestiging te stoppen."
                    vestiging={vestiging}
                    toelichting={toelichting}
                    setToelichting={setToelichting}
                    onVestigingUpdate={onVestigingUpdate}
                    type={type}
                />
            );
    }
}

type BehandelAanvraagDialogProps = {
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    title: string;
    description: string;
    vestiging: Vestiging;
    toelichting: string;
    type: BehandelAanvraagDialogType;
    setToelichting: Dispatch<SetStateAction<string>>;
    onVestigingUpdate: VestigingenUpdateHandler;
};

function BehandelAanvraagDialog({
    vestiging,
    open,
    onOpenChange,
    title,
    description,
    onVestigingUpdate,
    type,
    toelichting,
    setToelichting,
}: Readonly<BehandelAanvraagDialogProps>) {
    const [showWarning, setShowWarning] = useState(false);
    const updateVestiging = async (update: Partial<AangevraagdeVestiging>) => {
        await onVestigingUpdate({
            [vestiging.aangevraagdeVestiging!.id]: AangevraagdeVestiging.update(vestiging.aangevraagdeVestiging!, update),
        });
        onOpenChange(false);
    };

    const hasGaatNietLeverenToelichting = toelichting.trim().length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <DialogBody className="grid gap-4">
                    <DialogDescription>{description}</DialogDescription>
                    <VestigingCard vestiging={vestiging} />

                    <div>
                        <Label htmlFor="aanvraag-toelichting" className="mb-1">
                            Toelichting voor Regievoerder
                        </Label>
                        <Textarea
                            rows={3}
                            id="aanvraag-toelichting"
                            value={toelichting}
                            onChange={(e) => setToelichting(e.target.value)}
                            aria-invalid={!hasGaatNietLeverenToelichting && type === "Besteld"}
                        />
                    </div>
                    {type === "Besteld" && showWarning && !hasGaatNietLeverenToelichting && (
                        <FormErrorMessage>Voeg een toelichting toe.</FormErrorMessage>
                    )}
                </DialogBody>
                <DialogFooter className="flex flex-row justify-end gap-7">
                    {type === "Besteld" && (
                        <>
                            <Button
                                variant="tertiary"
                                destructive
                                onClick={() => {
                                    if (hasGaatNietLeverenToelichting) {
                                        void updateVestiging({
                                            status: "GaatNietLeveren",
                                            toelichtingKlantenservice: toelichting.trim(),
                                        });
                                        setShowWarning(false);
                                    } else {
                                        setShowWarning(true);
                                    }
                                }}
                            >
                                Gaat niet leveren
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => updateVestiging({ status: "Actief", toelichtingKlantenservice: toelichting })}
                            >
                                Activeren
                            </Button>
                        </>
                    )}
                    {type === "StopAangevraagd" && (
                        <Button
                            variant="primary"
                            destructive
                            onClick={() => updateVestiging({ status: "Gestopt", toelichtingKlantenservice: toelichting })}
                        >
                            Stoppen
                        </Button>
                    )}
                </DialogFooter>
            </DialogContainer>
        </Dialog>
    );
}

const behandelVerlengingSchema = z.object({
    extendedClosingTime: z.string().trim().regex(timeRegex, { error: "Voer een geldige tijd in (HH:MM)" }),
    toelichting: z.string().trim().optional(),
});

type BehandelVerlengingFormValues = z.infer<typeof behandelVerlengingSchema>;

type BehandelVerlengingDialogProps = {
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    title: string;
    description: string;
    vestiging: Vestiging;
    onVestigingUpdate: VestigingenUpdateHandler;
};

function BehandelVerlengingDialog({
    vestiging,
    open,
    onOpenChange,
    title,
    description,
    onVestigingUpdate,
}: Readonly<BehandelVerlengingDialogProps>) {
    const isStartVerzoek = vestiging.aangevraagdeVestiging?.status === VestigingStatus.BESTELD;
    const [acceptExtension, setAcceptExtension] = useState(true);

    const form = useForm<BehandelVerlengingFormValues>({
        resolver: zodResolver(behandelVerlengingSchema),
        defaultValues: {
            extendedClosingTime: vestiging.currentOpeningstijden.aangepasteOpenTot,
            toelichting: "",
        },
    });

    const handleSubmit = async (data: BehandelVerlengingFormValues) => {
        const updatedOpeningstijden = AangevraagdeVestiging.updateOpeningstijden(
            vestiging,
            data.extendedClosingTime,
            isStartVerzoek && !acceptExtension ? VerlengingStatus.AFGEWEZEN : VerlengingStatus.ACTIEF
        );

        await onVestigingUpdate({
            [vestiging.aangevraagdeVestiging!.id]: AangevraagdeVestiging.update(vestiging.aangevraagdeVestiging!, {
                status: VestigingStatus.ACTIEF,
                toelichtingKlantenservice: data.toelichting ?? "",
                openingstijden: updatedOpeningstijden,
            }),
        });
        onOpenChange(false);
    };

    const handleReject = () => {
        const toelichting = form.getValues("toelichting");
        if (toelichting?.trim().length === 0) {
            const message = isStartVerzoek
                ? "Toelichting is verplicht wanneer een vestiging niet gaat leveren."
                : "Toelichting is verplicht wanneer niet verlengd wordt.";
            form.setError("toelichting", { message });
            return;
        }

        const update: Partial<AangevraagdeVestiging> = { status: VestigingStatus.GAAT_NIET_LEVEREN };
        let updatedOpeningstijden: AangevraagdeVestigingOpeningstijd[];
        if (!isStartVerzoek) {
            updatedOpeningstijden = AangevraagdeVestiging.updateOpeningstijden(vestiging, null, VerlengingStatus.AFGEWEZEN);
            update.status = VestigingStatus.ACTIEF;
            update.openingstijden = updatedOpeningstijden;
        }

        update.toelichtingKlantenservice = toelichting ?? "";

        void onVestigingUpdate({
            [vestiging.aangevraagdeVestiging!.id]: AangevraagdeVestiging.update(vestiging.aangevraagdeVestiging!, update),
        });

        onOpenChange(false);
    };

    const showCheckbox = isStartVerzoek;
    const showExtensionInput = !isStartVerzoek || acceptExtension;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer className="max-w-md">
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(handleSubmit)(e)}>
                        <DialogHeader>
                            <DialogTitle>{title}</DialogTitle>
                        </DialogHeader>
                        <DialogBody className="grid gap-4">
                            <DialogDescription>{description}</DialogDescription>
                            <VestigingCard vestiging={vestiging} />

                            <div className="space-y-2">
                                <Label className="mb-1">Voorgestelde sluitingstijd regievoerder</Label>
                                <Input className="w-24" value={vestiging.currentOpeningstijden.aangepasteOpenTot} disabled />
                            </div>

                            {showCheckbox && (
                                <>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="accept-extension"
                                            checked={acceptExtension}
                                            onCheckedChange={(checked) => setAcceptExtension(checked === true)}
                                        />
                                        <Label
                                            htmlFor="accept-extension"
                                            className="text-m text-content-default cursor-pointer font-normal"
                                        >
                                            Vestiging blijft langer open
                                        </Label>
                                    </div>

                                    {!acceptExtension && (
                                        <div className="text-content-warning">
                                            <p className="text-s">
                                                Let op: De verlenging van openingstijden wordt afgewezen. De vestiging blijft open tot de
                                                reguliere sluitingstijd.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {showExtensionInput && (
                                <div className="space-y-2">
                                    <FormFieldInputTime
                                        form={form}
                                        name="extendedClosingTime"
                                        inputClassName="w-24"
                                        label="Afgesproken nieuwe sluitingstijd"
                                    />
                                </div>
                            )}

                            <TextAreaFormField rows={3} form={form} name="toelichting" label="Toelichting voor regievoerder" />
                        </DialogBody>
                        <DialogFooter className="flex flex-row justify-end gap-7">
                            <Button type="button" variant="tertiary" destructive onClick={handleReject}>
                                {isStartVerzoek ? "Gaat niet leveren" : "Niet verlengen"}
                            </Button>
                            <Button type="submit" variant="primary">
                                {isStartVerzoek ? "Activeren" : "Openingstijd verlengen"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
